import { Button } from '@platform/ui-library';
import { FC, useEffect, useState } from 'react';
import { FileUploaderModal, InputAcceptType } from '@app/form_components';
import { useDesign } from '@app/hooks';
import {
  CsvDropdown,
  IntentLabelTable,
  ObjectDropdown
} from '@app/page-components';
import { Design, ObjectClass } from '@app/types';
import {
  createCsv,
  createUniqueValue,
  createUuid,
  getIntentLabelColumns,
  removeDuplicates
} from '@app/utilities';

const createIntentLabelCsv = (serialized: Design) => {
  return createCsv({
    columns: getIntentLabelColumns(serialized),
    items: removeDuplicates({
      items: serialized.components.intents,
      key: 'name'
    })
  });
};

/** Settings page for HAU configuration. */
export const SettingsHauPage: FC = () => {
  const appManager = window.apm;
  const serialized = useDesign();
  const { csvData, rootSettings } = serialized;
  const [isIntentLabelModalOpen, setIsIntentLabelModalOpen] = useState(false);
  // Stored in state in addition to using @see useDesign to speed up the UI.
  const [disambiguationPromptId, setDisambiguationPromptId] = useState<string>();
  const [csvIntentLabelTableId, setCsvIntentLabelTableId] = useState<string>();

  useEffect(() => {
    setDisambiguationPromptId(rootSettings.disambiguationPromptId);
  }, [rootSettings.disambiguationPromptId]);

  useEffect(() => {
    setCsvIntentLabelTableId(rootSettings.csvIntentLabelTableId);
  }, [rootSettings.csvIntentLabelTableId]);

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="flex-column flex-gap">
          <ObjectDropdown
            className="form-component dropdown"
            isClearable
            label="Disambiguation Prompt"
            name="disambiguationPromptId"
            onChange={(selectedId) => {
              const newId = selectedId ?? undefined;
              const { serialized } = appManager;
              const { rootSettings } = serialized;
              rootSettings.disambiguationPromptId = newId;
              setDisambiguationPromptId(newId);
              rootSettings.lastLocalChange = appManager.timestamp;
              appManager.setSerializedChanged();
            }}
            tooltip="When N-best is enabled, this prompt asks the user which of three intents they mean; this can be overriden at the node level."
            type={ObjectClass.prompt}
            value={disambiguationPromptId ?? ''}
          />
          <div className="flex-gap">
            <CsvDropdown
              fieldConfig={{ className: 'flex-grow' }}
              isClearable
              label="Intent Label CSV"
              name="csvIntentLabelTableId"
              onChange={(selectedId) => {
                const newId = selectedId ?? undefined;
                const { serialized } = appManager;
                const { rootSettings } = serialized;
                rootSettings.csvIntentLabelTableId = newId;
                setCsvIntentLabelTableId(newId);
                rootSettings.lastLocalChange = appManager.timestamp;
                appManager.setSerializedChanged();
              }}
              value={csvIntentLabelTableId}
            />
            <div className="flex-column flex-end">
              <Button
                onClick={() => {
                  setIsIntentLabelModalOpen(true);
                }}
                theme='default'
              >
                Upload New CSV
              </Button>
            </div>
          </div>
          <FileUploaderModal
            accept={InputAcceptType.csv}
            isOpen={isIntentLabelModalOpen}
            labels={{ header: 'Upload Intent Label CSV' }}
            template={{
              fileName: 'intent_labels.csv',
              text: () => {
                return createIntentLabelCsv(serialized);
              }
            }}
            onComplete={(file) => {
              setIsIntentLabelModalOpen(false);
              if (!file) {
                return;
              }
              const { fileName, text } = file;
              const { serialized, timestamp } = appManager;
              const id = createUuid();
              const name = createUniqueValue({
                existing: csvData.map(({ name }) => {
                  return name;
                }),
                generate: (seed) => {
                  const addOn = (seed === 0) ? '' : `_${seed}`;
                  return `${fileName.replace(/\.csv$/, '')}${addOn}.csv`;
                }
              });
              serialized.csvData.push({
                createdTime: timestamp,
                id,
                lastLocalChange: timestamp,
                lastSavedChange: timestamp,
                name,
                value: text
              });
              serialized.rootSettings.csvIntentLabelTableId = id;
              serialized.rootSettings.lastLocalChange = timestamp;
              setCsvIntentLabelTableId(id);
              appManager.setSerializedChanged();
            }}
          />
          <IntentLabelTable />
        </div>
      </div>
    </div>
  );
};
